/**
 * Work plan edits — the controller.
 *
 * Every change saves at once through the existing ProductionPlan endpoints
 * and can be undone from the toast. A drop or move that lands on a taken
 * slot does not fail: it asks whether to replace (a product) or swap (two
 * cards) and does that in one go. The board's order list is kept in step
 * optimistically and corrected from the server's answer.
 */
import { useCallback, useMemo, useRef, useState } from "react";

import { LaborStandard } from "../../../../services/labor-standards-api";
import { productionPlanApi } from "../../../../services/production-plan-api";
import { Product } from "../../../../services/products-api";
import { CreateWorkOrderDto, ManufacturingOrder, UpdateWorkOrderDto } from "../utils/calculations";
import { activityOrderType, nextBatchLabel, planDrop, planMove, runMinutesOn, ShiftWindow, shiftWindowsFrom, standardsFor, toLocalIso } from "./plan-edits"; // eslint-disable-line sort-imports

export type PlanNotify = (message: string, severity?: "success" | "error" | "warning", undo?: () => void) => void;

/** What the toast says when a production order lands with no standard on its line. */
export const noStandardNote = (productName: string, workstation: string): string =>
	`No standard for ${productName} on ${workstation}: run time set to the shift. Add a standard to plan labor.`;

/** A question the board must answer before the edit goes ahead. */
export interface PlanAsk {
	title: string;
	message: string;
	confirmLabel: string;
	onConfirm: () => Promise<void>;
}

export interface UsePlanEditsArgs {
	orders: ManufacturingOrder[];
	setOrders: React.Dispatch<React.SetStateAction<ManufacturingOrder[]>>;
	standards: LaborStandard[];
	locationId?: number | null;
	notify: PlanNotify;
}

const patch = (order: ManufacturingOrder, changes: UpdateWorkOrderDto): ManufacturingOrder => ({ ...order, ...changes } as ManufacturingOrder);

const whenText = (startIso: string, shift: ShiftWindow | null | undefined): string => {
	const d = new Date(startIso);
	const day = d.toLocaleDateString(undefined, { weekday: "short", day: "numeric", month: "short" });
	return shift ? `${day} ${shift.label}` : day;
};

export function usePlanEdits({ orders, setOrders, standards, locationId, notify }: UsePlanEditsArgs) {
	const [ask, setAsk] = useState<PlanAsk | null>(null);
	const [busy, setBusy] = useState(false);
	const ordersRef = useRef(orders);
	ordersRef.current = orders;

	const shifts = useMemo(() => shiftWindowsFrom(orders), [orders]);

	const run = useCallback(
		async (work: () => Promise<void>, failure: string) => {
			setBusy(true);
			try {
				await work();
			} catch (error: unknown) {
				const message = (error as { response?: { data?: { message?: string } } })?.response?.data?.message;
				notify(message || failure, "error");
			} finally {
				setBusy(false);
			}
		},
		[notify]
	);

	// ---- create -------------------------------------------------------------

	const createOrder = useCallback(
		async (dto: CreateWorkOrderDto, label: string, warning?: string) => {
			const created = await productionPlanApi.create(dto);
			setOrders((prev) => [...prev, created]);
			notify(warning ? `Added ${label}. ${warning}` : `Added ${label}`, warning ? "warning" : "success", () => {
				void run(async () => {
					await productionPlanApi.delete(created.id);
					setOrders((prev) => prev.filter((o) => o.id !== created.id));
					notify(`Removed ${label}`, "success");
				}, "Could not undo.");
			});
		},
		[setOrders, notify, run]
	);

	// Plant-wide, so the number continues from the latest batch anywhere in
	// the plan, not just the week on screen. A failed lookup falls back to
	// what is loaded.
	const labelFor = useCallback(async (product: Product): Promise<string | null> => {
		if (activityOrderType(product)) return null;
		try {
			return await productionPlanApi.nextBatchLabel(product.id);
		} catch {
			return null;
		}
	}, []);

	const dropProduct = useCallback(
		async (product: Product, workstation: string, day: Date) => {
			const label = await labelFor(product);
			const plan = planDrop({
				product,
				workstation,
				day,
				orders: ordersRef.current,
				standards,
				shifts,
				locationId,
				orderType: activityOrderType(product) ?? "production",
				label,
			});
			const toastLabel = `${plan.order.sku} on ${workstation} · ${whenText(plan.order.startTime, plan.shift)}`;
			const warning = plan.hasStandard || plan.order.orderType !== "production" ? undefined : noStandardNote(product.name, workstation);
			if (plan.blockedBy) {
				const blocked = plan.blockedBy;
				setAsk({
					title: "Every shift on that line is taken",
					message: `Replace ${blocked.sku || blocked.productName || "the order"} (${whenText(blocked.startTime, shifts.find((s) => s.label === plan.shift.label))}) on ${workstation} with ${plan.order.sku}?`,
					confirmLabel: `Replace with ${plan.order.sku}`,
					onConfirm: () =>
						run(async () => {
							await productionPlanApi.delete(blocked.id);
							setOrders((prev) => prev.filter((o) => o.id !== blocked.id));
							await createOrder(plan.order, toastLabel, warning);
						}, "Could not replace the order."),
				});
				return;
			}
			void run(async () => {
				await createOrder(plan.order, toastLabel, warning);
			}, "Could not add the order.");
		},
		[standards, shifts, locationId, run, createOrder, setOrders, labelFor]
	);

	// ---- move ---------------------------------------------------------------

	const updateOrder = useCallback(
		async (order: ManufacturingOrder, changes: UpdateWorkOrderDto) => {
			// The update endpoint binds the whole order and insists the body's id
			// matches the route, so the id rides along with the changed fields.
			const dto = {
				id: order.id,
				productId: order.productId,
				sku: order.sku,
				quantity: order.quantity,
				workstation: order.workstation,
				startTime: order.startTime,
				endTime: order.endTime,
				laborStandardId: order.laborStandardId,
				operationId: order.operationId,
				batchNumber: order.batchNumber,
				locationId: order.locationId,
				attributeValues: order.attributeValues,
				orderType: order.orderType,
				...changes,
			} as UpdateWorkOrderDto;
			const saved = await productionPlanApi.update(order.id, dto);
			const next = saved && saved.id ? saved : patch(order, changes);
			setOrders((prev) => prev.map((o) => (o.id === order.id ? next : o)));
			return next;
		},
		[setOrders]
	);

	const moveOrder = useCallback(
		(order: ManufacturingOrder, workstation: string, day: Date) => {
			const plan = planMove({ order, workstation, day, orders: ordersRef.current, standards });
			const label = order.sku || order.productName || "the order";
			const before: UpdateWorkOrderDto = { workstation: order.workstation, startTime: order.startTime, endTime: order.endTime };
			const after: UpdateWorkOrderDto = { workstation: plan.workstation, startTime: plan.startTime, endTime: plan.endTime };
			const finish = (moved: ManufacturingOrder, undo: () => Promise<void>) => {
				notify(`Moved ${label} to ${workstation} · ${whenText(plan.startTime, null)}${plan.hasStandard ? "" : " · no standard on that line"}`, "success", () => {
					void run(undo, "Could not undo the move.");
				});
				return moved;
			};
			if (plan.blockedBy) {
				const other = plan.blockedBy;
				const otherBefore: UpdateWorkOrderDto = { workstation: other.workstation, startTime: other.startTime, endTime: other.endTime };
				setAsk({
					title: "That slot is taken",
					message: `${other.sku || other.productName || "Another order"} is already on ${workstation} then. Swap the two?`,
					confirmLabel: "Swap them",
					onConfirm: () =>
						run(async () => {
							// Park the other order first so the API's one-order-per-line rule is never tripped.
							const parked = await updateOrder(other, { workstation: `${other.workstation} (swap)`, startTime: other.startTime, endTime: other.endTime });
							const moved = await updateOrder(order, after);
							const swapped = await updateOrder(parked, { ...before });
							finish(moved, async () => {
								await updateOrder(swapped, { workstation: `${swapped.workstation} (swap)` });
								await updateOrder(moved, before);
								await updateOrder(swapped, otherBefore);
								notify(`Moved ${label} back`, "success");
							});
						}, "Could not swap the orders."),
				});
				return;
			}
			void run(async () => {
				const moved = await updateOrder(order, after);
				finish(moved, async () => {
					await updateOrder(moved, before);
					notify(`Moved ${label} back`, "success");
				});
			}, "Could not move the order.");
		},
		[standards, run, updateOrder, notify]
	);

	// ---- swap product -------------------------------------------------------

	const swapProduct = useCallback(
		async (order: ManufacturingOrder, product: Product) => {
			const current = ordersRef.current;
			const label = await labelFor(product);
			const standard = standardsFor(product, order.workstation, standards)[0];
			const minutes = runMinutesOn(product, order.workstation, standards);
			const start = new Date(order.startTime);
			const end = order.endTime ? new Date(order.endTime) : null;
			const shiftEnd = end && end > start ? end : new Date(start.getTime() + 8.5 * 3600000);
			const newEnd = minutes ? new Date(Math.min(start.getTime() + minutes * 60000, shiftEnd.getTime())) : shiftEnd;
			const kind = activityOrderType(product);
			const changes: UpdateWorkOrderDto = {
				productId: product.id,
				sku: kind ? product.name : label || nextBatchLabel(product, current.filter((o) => o.id !== order.id)),
				laborStandardId: standard?.id,
				endTime: toLocalIso(newEnd),
				orderType: kind ?? "production",
			};
			const before: UpdateWorkOrderDto = {
				productId: order.productId,
				sku: order.sku,
				laborStandardId: order.laborStandardId,
				endTime: order.endTime,
				orderType: order.orderType,
			};
			const was = order.sku || order.productName || "the order";
			void run(async () => {
				const saved = await updateOrder(order, changes);
				const unplanned = !standard && !kind;
				notify(`${was} is now ${changes.sku} on ${order.workstation}${unplanned ? `. ${noStandardNote(product.name, order.workstation)}` : ""}`, unplanned ? "warning" : "success", () => {
					void run(async () => {
						await updateOrder(saved, before);
						notify(`${was} is back`, "success");
					}, "Could not undo.");
				});
			}, "Could not change the product.");
		},
		[standards, run, updateOrder, notify, labelFor]
	);

	// ---- remove -------------------------------------------------------------

	const removeOrder = useCallback(
		(order: ManufacturingOrder) => {
			const label = order.sku || order.productName || "the order";
			const dto: CreateWorkOrderDto = {
				productId: order.productId,
				sku: order.sku,
				quantity: order.quantity,
				workstation: order.workstation,
				startTime: order.startTime,
				endTime: order.endTime,
				laborStandardId: order.laborStandardId,
				operationId: order.operationId,
				batchNumber: order.batchNumber,
				locationId: order.locationId,
				attributeValues: order.attributeValues,
				orderType: order.orderType,
			};
			void run(async () => {
				await productionPlanApi.delete(order.id);
				setOrders((prev) => prev.filter((o) => o.id !== order.id));
				notify(`Removed ${label}`, "success", () => {
					void run(async () => {
						const back = await productionPlanApi.create(dto);
						setOrders((prev) => [...prev, back]);
						notify(`${label} is back`, "success");
					}, "Could not restore the order.");
				});
			}, "Could not remove the order.");
		},
		[run, setOrders, notify]
	);

	const dismissAsk = useCallback(() => setAsk(null), []);
	const confirmAsk = useCallback(async () => {
		const pending = ask;
		setAsk(null);
		if (pending) await pending.onConfirm();
	}, [ask]);

	return { shifts, busy, ask, dismissAsk, confirmAsk, dropProduct, moveOrder, swapProduct, removeOrder };
}

export type PlanEdits = ReturnType<typeof usePlanEdits>;
